/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import "./CompanyDetails.css";

import React from "react";

import { bindActionCreators, Dispatch } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import { CompaniesState, slice as companiesSlice } from "store/company/companies.slice";

import { CompanyDetailsProps, CompanyDetailsState } from "./CompanyDetails.types";
import Input from "components/ThreeKey/Input";
import InputDropDown from "components/ThreeKey/InputDropDown";
import countries from "data/countries/en_US";


class CompanyDetails extends React.Component<CompanyDetailsProps, CompanyDetailsState> {

   private _countries: string[];

   constructor(props: CompanyDetailsProps) {

      super(props);

      this.state = {
         companyName: "",
         addressLine1: "",
         addressLine2: "",
         city: "",
         state: "",
         country: "",
         zip: "",
         taxId: "",
         vatId: ""
      }

      this._countries = [];
      for (const countryCode in countries) this._countries.push(countries[countryCode]);

   }

   public componentDidMount(): void {

      if (this.props.companies.selectedCompany) this.props.actions.loadCompany({ id: this.props.companies.selectedCompany.id });

   }

   public componentWillUnmount(): void {
   }

   public componentDidUpdate(prevProps: CompanyDetailsProps, prevState: CompanyDetailsState) {

      if (
         (this.props.companies.companyLoading !== prevProps.companies.companyLoading && !this.props.companies.companyLoading) ||
         (this.props.companies.selectedCompany !== prevProps.companies.selectedCompany)
      ) {

         this.setState({
            companyName: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.companyName : "",
            addressLine1: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.addressStreet1 : "",
            addressLine2: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.addressStreet2 : "",
            city: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.addressCity : "",
            state: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.addressState : "",
            country: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.addressCountry : "",
            zip: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.addressZip : "",
            taxId: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.taxId : "",
            vatId: this.props.companies.selectedCompany ? this.props.companies.selectedCompany.taxVatId : ""
         })

      }

      if (this.state !== prevState) {
         const hasChanged = this.hasDataChanged()
         this.props.notifyDataChanges(hasChanged);
      }

   }

   public render(): React.ReactNode {

      const saveEnabled = !this.props.companies.companyLoading && !this.props.companies.companyUpdating && this.hasDataChanged();

      return (

         <div className="dialog-company-details">

            <div className="container">

               <h4>Company details</h4>

               <table>

                  <tbody>

                     <tr>
                        <td><label htmlFor="company_name">Company name:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_name"
                              value={this.state.companyName}
                              onChange={(val) => this.setState({ companyName: val.toString() }) }
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                     <tr>
                        <td colSpan={2}>
                           <br />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_address_1">Address line 1:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_address_1"
                              value={this.state.addressLine1}
                              onChange={(val) => this.setState({ addressLine1: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_address_2">Address line 2:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_address_2"
                              value={this.state.addressLine2}
                              onChange={(val) => this.setState({ addressLine2: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_city">City:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_city"
                              value={this.state.city}
                              onChange={(val) => this.setState({ city: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_state">State:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_state"
                              value={this.state.state}
                              onChange={(val) => this.setState({ state: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_postal_code">Postal code:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_postal_code"
                              value={this.state.zip}
                              onChange={(val) => this.setState({ zip: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_country">Country:</label></td>
                        <td>
                           <InputDropDown
                              componentName="company-details"
                              id="company_country"
                              items={this._countries}
                              value={this.state.country}
                              onChange={(val) => this.setState({ country: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              dropDownOnKeyDown={true}
                           />
                        </td>
                     </tr>

                     <tr>
                        <td colSpan={2}>
                           <br />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_tax_id">Tax ID:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_tax_id"
                              value={this.state.taxId}
                              onChange={(val) => this.setState({ taxId: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                     <tr>
                        <td><label htmlFor="company_vat_id">VAT ID:</label></td>
                        <td>
                           <Input
                              cssSuffix="company-details"
                              id="company_vat_id"
                              value={this.state.vatId}
                              onChange={(val) => this.setState({ vatId: val.toString() })}
                              readOnly={this.props.companies.companyUpdating}
                              autoComplete="off"
                           />
                        </td>
                     </tr>

                  </tbody>

               </table>

               <div className="spinner" data-visible={this.props.companies.companyLoading || this.props.companies.companyUpdating}></div>

            </div>

            <div className="buttons">

               <button
                  data-type="ok"
                  onMouseDown={this.saveChanges}
                  disabled={!saveEnabled}
               >Save changes</button>

            </div>

         </div>

      )

   }

   private saveChanges = (event: React.MouseEvent) => {

      event.preventDefault();
      if (!this.props.companies.selectedCompany) return;

      this.props.actions.updateCompany({
         company: {
            _seq_no: this.props.companies.selectedCompany?._seq_no,
            _primary_term: this.props.companies.selectedCompany?._primary_term,
            id: this.props.companies.selectedCompany.id,
            companyName: this.state.companyName,
            addressStreet1: this.state.addressLine1,
            addressStreet2: this.state.addressLine2,
            addressCity: this.state.city,
            addressState: this.state.state,
            addressZip: this.state.zip,
            addressCountry: this.state.country,
            taxId: this.state.taxId,
            taxVatId: this.state.vatId,
            technicalContactName: "",
            technicalContactEmail: "",
            financialContactName: "",
            financialContactEmail: ""
         }
      })
   }

   private hasDataChanged(): boolean {
      return (
         this.state.companyName !== this.props.companies.selectedCompany?.companyName ||
         this.state.addressLine1 !== this.props.companies.selectedCompany?.addressStreet1 ||
         this.state.addressLine2 !== this.props.companies.selectedCompany?.addressStreet2 ||
         this.state.city !== this.props.companies.selectedCompany?.addressCity ||
         this.state.state !== this.props.companies.selectedCompany?.addressState ||
         this.state.zip !== this.props.companies.selectedCompany?.addressZip ||
         this.state.country !== this.props.companies.selectedCompany?.addressCountry ||
         this.state.taxId !== this.props.companies.selectedCompany?.taxId ||
         this.state.vatId !== this.props.companies.selectedCompany?.taxVatId
      );
   }

}


const mapStateToProps = (state: {
   companies: CompaniesState, // redux state interface
}) => {
   return state;
}


const mapDispatchToProps = (dispatch: Dispatch) => {

   return {
      actions: bindActionCreators(
         Object.assign(
            {},
            companiesSlice.actions
         ),
         dispatch
      )
   }

}

export default connect(
   mapStateToProps,
   mapDispatchToProps
)(CompanyDetails);